import { useState, useEffect } from 'react';
import { Plus, Gift, Clock, PlusCircle, CreditCard, RotateCcw, type LucideIcon } from 'lucide-react';
import { walletService, type Transaction } from '../services/walletService';
import { formatEuro } from '../utils/currency';
// Sentry removed

function formatDate(dateStr: string): string {
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

interface MovementDisplay {
    Icon: LucideIcon;
    iconBg: string;
    iconColor: string;
    amountColor: string;
    title: string;
    prefix: string;
    amountText: string;
    opacityClass: string;
}

/**
 * Map a transaction to its visual treatment. Money movements (saldo en €)
 * get their own icons, colours, Spanish labels and € amounts; points
 * movements keep the original pts styling.
 */
function getMovementDisplay(tx: Transaction): MovementDisplay {
    const isMoney = tx.currency === 'money';
    const amountText = isMoney ? formatEuro(Math.abs(tx.amount)) : Math.abs(tx.amount).toLocaleString();

    switch (tx.transactionType) {
        case 'TopUp':
            return {
                Icon: PlusCircle,
                iconBg: 'bg-emerald-100',
                iconColor: 'text-emerald-600',
                amountColor: 'text-emerald-600',
                title: 'Recarga',
                prefix: '+',
                amountText,
                opacityClass: '',
            };
        case 'Spend':
            return {
                Icon: CreditCard,
                iconBg: 'bg-indigo-100',
                iconColor: 'text-indigo-600',
                amountColor: 'text-indigo-600',
                title: 'Pago con saldo',
                prefix: '-',
                amountText,
                opacityClass: '',
            };
        case 'Redeem':
            return {
                Icon: Gift,
                iconBg: 'bg-gray-100',
                iconColor: 'text-gray-600',
                amountColor: 'text-gray-600',
                title: tx.detail || 'Premio canjeado',
                prefix: '-',
                amountText,
                opacityClass: '',
            };
        case 'Expire':
            return {
                Icon: Clock,
                iconBg: 'bg-orange-50',
                iconColor: 'text-orange-500',
                amountColor: 'text-orange-400',
                title: 'Puntos caducados',
                prefix: '-',
                amountText,
                opacityClass: 'opacity-80',
            };
        case 'Void':
            // Money Void returns saldo to the purse (+); points Void reverses earned points (-).
            return {
                Icon: RotateCcw,
                iconBg: 'bg-amber-50',
                iconColor: 'text-amber-600',
                amountColor: isMoney ? 'text-emerald-600' : 'text-gray-600',
                title: isMoney ? 'Anulación' : 'Puntos anulados',
                prefix: isMoney ? '+' : '-',
                amountText,
                opacityClass: 'opacity-90',
            };
        case 'Earn':
        default:
            return {
                Icon: Plus,
                iconBg: 'bg-emerald-100',
                iconColor: 'text-emerald-600',
                amountColor: 'text-emerald-600',
                title: 'Puntos obtenidos',
                prefix: '+',
                amountText,
                opacityClass: '',
            };
    }
}

export default function History() {
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const data = await walletService.getUserHistory();
                // Interleave points and money movements chronologically (newest first).
                const sorted = [...data].sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                setTransactions(sorted);
            } catch (err) {
                // Removed expected error capture
                console.error('Error fetching history:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchHistory();
    }, []);

    return (
        <div className="flex flex-col">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white/95 px-5 pb-3 pt-12 shadow-sm backdrop-blur-sm">
                <h1 className="text-xl font-bold text-gray-800">Historial</h1>
            </div>

            {/* Transaction List */}
            <div className="flex flex-col gap-3 overflow-y-auto px-5 py-4">
                {isLoading ? (
                    /* Skeleton */
                    <>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div
                                key={i}
                                className="flex animate-pulse items-center gap-4 rounded-2xl bg-white p-4 shadow-sm"
                            >
                                <div className="h-10 w-10 shrink-0 rounded-full bg-gray-200" />
                                <div className="flex-1">
                                    <div className="mb-2 h-4 w-28 rounded bg-gray-200" />
                                    <div className="h-3 w-20 rounded bg-gray-100" />
                                </div>
                                <div className="h-5 w-14 rounded bg-gray-200" />
                            </div>
                        ))}
                    </>
                ) : transactions.length === 0 ? (
                    /* Empty state */
                    <div className="mt-10 flex flex-col items-center gap-3 text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                            <Clock size={28} className="text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-400">
                            Aún no tienes movimientos.
                        </p>
                    </div>
                ) : (
                    /* Transaction items */
                    transactions.map((tx, index) => {
                        const { Icon, iconBg, iconColor, amountColor, title, prefix, amountText, opacityClass } =
                            getMovementDisplay(tx);

                        return (
                            <div
                                key={tx.id ?? index}
                                className={`flex items-center gap-4 rounded-2xl bg-white p-4 shadow-sm ${opacityClass}`}
                            >
                                {/* Icon */}
                                <div
                                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconBg}`}
                                >
                                    <Icon size={20} className={iconColor} />
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">
                                        {title}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {tx.brandName ?? 'Desconocido'} · {formatDate(tx.date)}
                                    </p>
                                </div>

                                {/* Amount */}
                                <p
                                    className={`shrink-0 font-bold ${amountColor}`}
                                >
                                    {prefix}
                                    {amountText}
                                </p>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
